import { useEffect, useState } from "react";
import type { AiAssistCoachReply as CoachReplyDraft } from "@care-by-ella/contracts";
import { MEDICAL_REVIEW_NEUTRAL_STATUS } from "@care-by-ella/contracts";
import {
  AiAssistDisabledError,
  checkinActionErrorText,
  fetchAiCoachReply,
  generateAiCoachReply,
  sendCheckinCoachReply,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-710 — the coach's reply draft under one weekly check-in. Reflect and
// encourage only; advice is the coach's edit. The text is a plain textarea
// the coach owns from the moment it appears. TES-899 —
// "Skicka svar och slutför"
// sends whatever is in the box as an ordinary coach message (no
// marker, Simon's decision 2026-09-16) through the check-in's own action: the
// message is linked to the check-in, the check-in is completed, the draft is
// recorded as used, all in one request, and a repeat cannot send it twice.
// A doctor-routed check-in gets no draft at all.

const TEXTAREA =
  "w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

function errorTextFor(e: unknown): string {
  const code = e instanceof Error ? e.message : "";
  switch (code) {
    case "ai_assist_provider_unavailable":
      return "Modellen svarade inte. Försök igen.";
    case "ai_assist_output_rejected":
      return "Utkastet stoppades av skyddsregeln: texten innehöll råd eller något som inte stod i incheckningen. Försök igen.";
    case "ai_assist_no_input":
      return "Incheckningen har inga svar att utgå från.";
    default:
      return "Kunde inte ta fram ett utkast. Försök igen.";
  }
}

function AiAssistCoachReplyPanel({
  patientId,
  submissionId,
  onUnauthorized,
  onDisabled,
  onHandled,
}: {
  patientId: string;
  submissionId: string;
  onUnauthorized: () => void;
  onDisabled: () => void;
  // The check-in is now answered; the parent reloads and drops this box.
  onHandled: () => void;
}) {
  const [data, setData] = useState<CoachReplyDraft | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<"generate" | "send" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAiCoachReply(patientId, submissionId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (d.reply) setText(d.reply);
      })
      .catch((e: unknown) => {
        if (e instanceof AiAssistDisabledError) onDisabled();
        else if (e instanceof UnauthorizedError) onUnauthorized();
        else if (!cancelled) setError("Kunde inte hämta utkastet.");
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, submissionId, onUnauthorized, onDisabled]);

  async function generate() {
    setBusy("generate");
    setError(null);
    try {
      const d = await generateAiCoachReply(patientId, submissionId);
      setData(d);
      if (d.reply) setText(d.reply);
      setSentAt(null);
    } catch (e) {
      if (e instanceof AiAssistDisabledError) onDisabled();
      else if (e instanceof UnauthorizedError) onUnauthorized();
      else setError(errorTextFor(e));
    } finally {
      setBusy(null);
    }
  }

  async function send() {
    const body = text.trim();
    if (!body || !data?.draft) return;
    setBusy("send");
    setError(null);
    try {
      const res = await sendCheckinCoachReply(patientId, submissionId, {
        body,
        aiDraftId: data.draft.id,
      });
      setSentAt(res.handledAt);
      onHandled();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError(checkinActionErrorText(e));
    } finally {
      setBusy(null);
    }
  }

  if (!data && !error) return null;

  return (
    <div className="mt-sm rounded-md border border-surfaceMuted bg-surfaceMuted/40 p-sm">
      <div className="flex items-baseline justify-between gap-sm">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
          Svarsutkast
        </h4>
        <span className="text-[11px] uppercase tracking-wide text-textSecondary">
          AI-stöd
        </span>
      </div>

      {error ? <p className="mt-xs text-sm text-danger">{error}</p> : null}

      {data?.blocked ? (
        <p className="mt-xs text-sm text-textSecondary">
          {MEDICAL_REVIEW_NEUTRAL_STATUS}
        </p>
      ) : data ? (
        <>
          {data.draft ? (
            <>
              <p className="mt-xs text-xs text-textSecondary">
                Speglar bara det patienten skrev. Råd och egna ord lägger du
                till själv innan du skickar.
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                className={`mt-xs ${TEXTAREA}`}
                aria-label="Svarsutkast"
              />
              {data.appended.length > 0 ? (
                <p className="mt-xs text-xs text-textSecondary">
                  Sista meningen lades till av systemet: svaret som gått till
                  läkaren syns inte här.
                </p>
              ) : null}
            </>
          ) : (
            <p className="mt-xs text-sm text-textSecondary">Inget utkast än.</p>
          )}
          <div className="mt-sm flex flex-wrap items-center gap-sm">
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void generate()}
              className="rounded-md border border-brand px-md py-xs text-sm font-semibold text-brand disabled:opacity-50"
            >
              {busy === "generate"
                ? "Tar fram…"
                : data.draft
                  ? "Ta fram igen"
                  : "Ta fram utkast"}
            </button>
            {data.draft ? (
              <button
                type="button"
                disabled={
                  busy !== null || text.trim() === "" || sentAt !== null
                }
                onClick={() => void send()}
                className="rounded-md bg-brand px-md py-xs text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy === "send"
                  ? "Skickar…"
                  : sentAt
                    ? "Skickat"
                    : "Skicka svar och slutför"}
              </button>
            ) : null}
            {sentAt ? (
              <span className="text-xs text-textSecondary">
                Skickat{" "}
                {new Date(sentAt).toLocaleTimeString("sv-SE", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

// TES-1040 — hidden in the admin's read-only support view: AI generation and sending are out of scope for the support views.
export const AiAssistCoachReply = hiddenWhenReadOnly(AiAssistCoachReplyPanel);
